/**
 * Trainer publishes session clock; students subscribe so Expected end stays in sync.
 * Uses useMaybeRoomContext — safe while token loads (before LiveKitRoom mounts).
 *
 * Flow:
 *   step 1) Student listens for clock payloads
 *   step 2) Trainer publishes on change + every 15s heartbeat
 */

import { useEffect, useRef, useState } from "react";
import { useMaybeRoomContext } from "@livekit/components-react";
import { RoomEvent } from "livekit-client";
import {
  decodeLiveSessionClock,
  encodeLiveSessionClock,
  stabilizeProjectedEndTime,
  stabilizeQaPaused,
  type LiveSessionClockMessage,
} from "./liveSessionClockSync";

type ClockFields = Omit<LiveSessionClockMessage, "type" | "v">;

type UseLiveSessionClockSyncArgs = {
  role: "trainer" | "student";
  enabled?: boolean;
  /** Trainer-only: fields to broadcast. */
  publish?: ClockFields | null;
};

export function useLiveSessionClockSync({
  role,
  enabled = true,
  publish = null,
}: UseLiveSessionClockSyncArgs): LiveSessionClockMessage | null {
  const room = useMaybeRoomContext();
  const [remote, setRemote] = useState<LiveSessionClockMessage | null>(null);
  const lastSentRef = useRef("");
  const lastAppliedRef = useRef<{
    topicId: string | null;
    subId: string | null;
    topicElapsed: number;
    subElapsed: number;
    sessionElapsed: number;
    pauseAccumulated: number;
  }>({
    topicId: null,
    subId: null,
    topicElapsed: 0,
    subElapsed: 0,
    sessionElapsed: 0,
    pauseAccumulated: 0,
  });

  useEffect(() => {
    if (!enabled || role !== "student" || !room) return;

    const onData = (payload: Uint8Array) => {
      const msg = decodeLiveSessionClock(payload);
      if (!msg) return;
      setRemote((prev) => {
        const topicId = msg.currentTopicId ? String(msg.currentTopicId) : null;
        const subId = msg.currentSubtopicId
          ? String(msg.currentSubtopicId)
          : null;
        const last = lastAppliedRef.current;
        const topicChanged = Boolean(topicId) && topicId !== last.topicId;
        const subChanged = Boolean(subId) && subId !== last.subId;
        const topicElapsed = topicChanged
          ? Math.max(0, msg.topicElapsedSec || 0)
          : Math.max(last.topicElapsed, Math.max(0, msg.topicElapsedSec || 0));
        const subElapsed = subChanged
          ? Math.max(0, msg.subtopicElapsedSec || 0)
          : Math.max(
              last.subElapsed,
              Math.max(0, msg.subtopicElapsedSec || 0),
            );
        const sessionElapsed = Math.max(
          last.sessionElapsed,
          Math.max(0, msg.sessionElapsedSec || 0),
        );
        const pauseAccumulated = Math.max(
          last.pauseAccumulated,
          Math.max(0, msg.pauseAccumulatedSec || 0),
        );
        if (
          prev &&
          !topicChanged &&
          !subChanged &&
          topicElapsed === last.topicElapsed &&
          subElapsed === last.subElapsed &&
          sessionElapsed === last.sessionElapsed &&
          pauseAccumulated === last.pauseAccumulated &&
          msg.topicElapsedSec < last.topicElapsed
        ) {
          return prev;
        }
        lastAppliedRef.current = {
          topicId: topicId || last.topicId,
          subId: subId || last.subId,
          topicElapsed,
          subElapsed,
          sessionElapsed,
          pauseAccumulated,
        };
        const bonusChanged =
          Math.max(0, msg.topicTimeBonusSec || 0) !==
          Math.max(0, prev?.topicTimeBonusSec || 0);
        const qaPaused = stabilizeQaPaused(Boolean(prev?.qaPaused), msg, {
          sessionElapsed,
          pauseAccumulated,
        });
        const qaPausedChanged = qaPaused !== Boolean(prev?.qaPaused);
        const completedCountChanged =
          (msg.completedTopicIds?.length ?? 0) !==
          (prev?.completedTopicIds?.length ?? 0);
        const projectedEndTime = stabilizeProjectedEndTime(
          prev?.projectedEndTime,
          msg.projectedEndTime,
          {
            topicChanged,
            subChanged,
            bonusChanged,
            qaPausedChanged,
            completedCountChanged,
          },
        );
        return {
          ...msg,
          qaPaused,
          projectedEndTime,
          topicElapsedSec: topicElapsed,
          subtopicElapsedSec: subElapsed,
          sessionElapsedSec: sessionElapsed,
          pauseAccumulatedSec: pauseAccumulated,
          currentSubtopicActualStartAt: subChanged
            ? msg.currentSubtopicActualStartAt || null
            : msg.currentSubtopicActualStartAt ||
              prev?.currentSubtopicActualStartAt ||
              null,
          currentSubtopicId:
            subId ||
            (msg.topicStarted
              ? prev?.currentSubtopicId || null
              : msg.currentSubtopicId || null),
        };
      });
    };

    room.on(RoomEvent.DataReceived, onData);
    return () => {
      room.off(RoomEvent.DataReceived, onData);
    };
  }, [enabled, role, room]);

  useEffect(() => {
    if (!enabled || role !== "trainer" || !room || !publish) return;

    const send = (reliable: boolean) => {
      if (!room.localParticipant) return;
      void room.localParticipant
        .publishData(encodeLiveSessionClock(publish), { reliable })
        .then(() => {
          lastSentRef.current = JSON.stringify(publish);
        })
        .catch(() => {
          /* ignore transient publish errors */
        });
    };

    const key = JSON.stringify(publish);
    if (key !== lastSentRef.current) {
      send(true);
    }

    const id = window.setInterval(() => send(true), 15000);
    return () => window.clearInterval(id);
  }, [enabled, role, room, publish]);

  return role === "student" ? remote : null;
}
